import { Offer, OfferVariant, OrderItem } from 'src/app/stores/stores';

/**
 * "Maximum items per slot" (#9840): how many slot-counted items a basket puts
 * into a slot. Mirrors the backend's `SlotItemCountResolver` so the basket
 * check and the submit check agree on every value of "Count against slot
 * count". The backend stays authoritative: it rejects the order on submit with
 * ORDER_MAX_ITEMS_PER_SLOT_LIMIT_REACHED whatever this check decided.
 */
export const COUNT_AGAINST_SLOT = 'COUNT_AGAINST_SLOT';

/** A catalog attribute as the public catalog sends it (`attributeDtos`). */
export interface SlotCountAttribute {
  key: string;
  value?: unknown;
}

/**
 * One basket line, or one option / combo item inside a line, reduced to what
 * the slot count needs. `quantity` is per unit of the parent (a top-level line
 * carries the units in the basket).
 */
export interface SlotCountedItem {
  quantity: number;
  attributes?: SlotCountAttribute[];
  /** The selected price variant's own attributes; it is the same sale as the product. */
  variantAttributes?: SlotCountAttribute[];
  /**
   * The per-unit value the order response already resolved for a stored line
   * (variant folded in) or option: null when the offer has no
   * COUNT_AGAINST_SLOT attribute, undefined when the backend does not send it
   * and the attributes above are read instead.
   */
  countAgainstSlot?: number | null;
  options?: SlotCountedItem[];
}

/** A requested or stored order line: an AddOrderItem / UpdateOrderItem payload or a basket item. */
export interface SlotCountedRequest {
  offerId: number;
  quantity: number;
  variantOfferId?: number;
  /** Sent on stored lines by the order response (see SlotCountedItem). */
  countAgainstSlot?: number | null;
  childItemRequests?: SlotCountedRequest[];
  childOrderItems?: SlotCountedRequest[];
}

/** Catalog attributes by offer id; variants also record the product they belong to. */
export interface SlotCountIndexEntry {
  attributes?: SlotCountAttribute[];
  variantIds: Set<number>;
}
export type SlotCountIndex = Map<number, SlotCountIndexEntry>;

/** The store setting and the selected slot's booked items, when both are known. */
export interface SlotItemCapacity {
  maxItemsPerSlot: number;
  totalItems: number;
}

export default class SlotItemCountUtils {
  /**
   * Units one ordered unit contributes: 0 when missing or blank, 1 for "true",
   * 0 for "false", the number for a positive integer, 0 for anything else.
   */
  static resolveCountAgainstSlot(raw: unknown): number {
    if (raw === null || raw === undefined) {
      return 0;
    }
    const value = String(raw).trim();
    if (value === '') {
      return 0;
    }
    if (value.toLowerCase() === 'true') {
      return 1;
    }
    if (!/^\d+$/.test(value)) {
      return 0;
    }
    const parsed = parseInt(value, 10);
    return parsed > 0 ? parsed : 0;
  }

  static countAgainstSlotOf(attributes?: SlotCountAttribute[]): number {
    return SlotItemCountUtils.resolveCountAgainstSlot(
      SlotItemCountUtils.findCountAttribute(attributes)?.value,
    );
  }

  /** Slot items of one item, times the units of its parent. */
  static itemSlotCount(item: SlotCountedItem, parentUnits = 1): number {
    const units = (item?.quantity > 0 ? item.quantity : 0) * parentUnits;
    if (units === 0) {
      return 0;
    }
    const own = units * SlotItemCountUtils.unitValueOf(item);
    return (item.options ?? []).reduce(
      (total, option) =>
        total + SlotItemCountUtils.itemSlotCount(option, units),
      own,
    );
  }

  static basketSlotCount(items: SlotCountedItem[]): number {
    return (items ?? []).reduce(
      (total, item) => total + SlotItemCountUtils.itemSlotCount(item),
      0,
    );
  }

  /** Slot items of requested or stored order lines. */
  static requestsSlotCount(
    lines: SlotCountedRequest[],
    index: SlotCountIndex,
  ): number {
    return SlotItemCountUtils.basketSlotCount(
      (lines ?? []).map((line) =>
        SlotItemCountUtils.toCountedItem(line, index),
      ),
    );
  }

  /**
   * Indexes the attributes of the catalog offers, their price variants and
   * their option / combo items, so a basket line can be counted from its ids.
   */
  static buildIndex(offers: Offer[]): SlotCountIndex {
    const index: SlotCountIndex = new Map();
    const addOffer = (
      offer: Offer | OfferVariant,
      variants?: OfferVariant[],
    ) => {
      if (!offer?.offerId) {
        return;
      }
      const entry = index.get(offer.offerId) ?? { variantIds: new Set() };
      entry.attributes = entry.attributes ?? offer.attributeDtos;
      (variants ?? []).forEach((variant) => {
        if (variant?.offerId && variant.offerId !== offer.offerId) {
          entry.variantIds.add(variant.offerId);
        }
      });
      index.set(offer.offerId, entry);
      (variants ?? []).forEach((variant) => {
        if (variant?.offerId !== offer.offerId) {
          addOffer(variant);
        }
      });
      (offer.categories ?? []).forEach((category) =>
        (category?.offers ?? []).forEach((child) =>
          addOffer(child, child.variants),
        ),
      );
    };
    (offers ?? []).forEach((offer) => addOffer(offer, offer?.variants));
    return index;
  }

  /**
   * Reduces a requested or stored order line to a SlotCountedItem. A stored
   * line that carries `countAgainstSlot` is counted from it: the order
   * response folds the price variant into the line and lists only options as
   * children. Without the field (an older backend) the catalog index is read.
   */
  static toCountedItem(
    request: SlotCountedRequest,
    index: SlotCountIndex,
  ): SlotCountedItem {
    if (request.countAgainstSlot !== undefined) {
      return {
        quantity: request.quantity,
        countAgainstSlot: request.countAgainstSlot,
        options: [
          ...(request.childItemRequests ?? []),
          ...(request.childOrderItems ?? []),
        ].map((child) => SlotItemCountUtils.toCountedItem(child, index)),
      };
    }
    const entry = index.get(request.offerId);
    const isVariant = (offerId: number) =>
      offerId === request.variantOfferId ||
      (entry?.variantIds.has(offerId) ?? false);
    const children = [
      ...(request.childItemRequests ?? []),
      ...(request.childOrderItems ?? []),
    ];
    // The variant child of a request carries the line's units, not a per-unit
    // quantity, and is the same sale as the product: it is never an option.
    const variantId =
      request.variantOfferId ??
      children.find((child) => isVariant(child.offerId))?.offerId;
    return {
      quantity: request.quantity,
      attributes: entry?.attributes,
      variantAttributes:
        variantId != null ? index.get(variantId)?.attributes : undefined,
      options: children
        .filter((child) => !isVariant(child.offerId))
        .map((child) => SlotItemCountUtils.toCountedItem(child, index)),
    };
  }

  /**
   * A copy of the update request `request` for counting only, where every
   * offer the catalog index does not know (an option of a closed item page)
   * takes the `countAgainstSlot` the order response sent for the same offer in
   * `stored`, the line being updated. Offers the index knows are still
   * resolved from it.
   */
  static withStoredCounts(
    request: SlotCountedRequest,
    stored: SlotCountedRequest | undefined,
    index: SlotCountIndex,
  ): SlotCountedRequest {
    if (!stored || stored.offerId !== request.offerId) {
      return request;
    }
    const storedChildren = [
      ...(stored.childOrderItems ?? []),
      ...(stored.childItemRequests ?? []),
    ];
    const fill = (
      line: SlotCountedRequest,
      match: SlotCountedRequest | undefined,
    ): SlotCountedRequest =>
      match && !index.has(line.offerId) && line.countAgainstSlot === undefined
        ? { ...line, countAgainstSlot: match.countAgainstSlot }
        : line;
    const filled = fill(request, stored);
    return {
      ...filled,
      variantOfferId: filled.variantOfferId ?? stored.variantOfferId,
      childItemRequests: (request.childItemRequests ?? []).map((child) =>
        SlotItemCountUtils.withStoredCounts(
          child,
          storedChildren.find((s) => s.offerId === child.offerId),
          index,
        ),
      ),
      childOrderItems: (request.childOrderItems ?? []).map((child) =>
        SlotItemCountUtils.withStoredCounts(
          child,
          storedChildren.find((s) => s.offerId === child.offerId),
          index,
        ),
      ),
    };
  }

  /**
   * The capacity the basket is checked against, or null when there is no check:
   * MAX_ITEMS_PER_SLOT missing or 0, no selected slot, or a slot without
   * `totalItems` (a backend that does not send it yet).
   */
  static capacityOf(
    settings: { [key: string]: any } | null | undefined,
    selectedSlot:
      | { startTime?: string; totalItems?: number }
      | null
      | undefined,
  ): SlotItemCapacity | null {
    const maxItemsPerSlot = Number(settings?.MAX_ITEMS_PER_SLOT);
    if (!(maxItemsPerSlot > 0)) {
      return null;
    }
    if (
      !selectedSlot?.startTime ||
      typeof selectedSlot.totalItems !== 'number'
    ) {
      return null;
    }
    return { maxItemsPerSlot, totalItems: selectedSlot.totalItems };
  }

  /** Items the slot can still take; never negative. */
  static remaining(capacity: SlotItemCapacity): number {
    return Math.max(0, capacity.maxItemsPerSlot - capacity.totalItems);
  }

  /**
   * Whether a basket change must be refused: it adds slot items and the slot
   * cannot take the basket it would leave behind.
   */
  static exceedsCapacity(
    capacity: SlotItemCapacity | null,
    currentCount: number,
    projectedCount: number,
  ): boolean {
    if (!capacity || projectedCount <= currentCount) {
      return false;
    }
    return capacity.totalItems + projectedCount > capacity.maxItemsPerSlot;
  }

  /**
   * The basket's slot items with the line `itemUuid` replaced by `line`, or
   * `line` added when `itemUuid` is not given.
   */
  static projectedBasket(
    basket: OrderItem[],
    line: SlotCountedRequest,
    itemUuid?: string,
  ): SlotCountedRequest[] {
    const lines: SlotCountedRequest[] = (basket ?? []).filter(
      (item) => !itemUuid || item.uuid !== itemUuid,
    );
    return [...lines, line];
  }

  /**
   * Units one ordered unit of the item takes: the resolved `countAgainstSlot`
   * when the order response sent it (null = no attribute = 0), otherwise the
   * variant's own attribute when it has one, else the product's.
   */
  private static unitValueOf(item: SlotCountedItem): number {
    if (item.countAgainstSlot !== undefined) {
      return SlotItemCountUtils.resolveCountAgainstSlot(item.countAgainstSlot);
    }
    const ownAttributes = SlotItemCountUtils.findCountAttribute(
      item.variantAttributes,
    )
      ? item.variantAttributes
      : item.attributes;
    return SlotItemCountUtils.countAgainstSlotOf(ownAttributes);
  }

  private static findCountAttribute(
    attributes?: SlotCountAttribute[],
  ): SlotCountAttribute | undefined {
    return (attributes ?? []).find((a) => a?.key === COUNT_AGAINST_SLOT);
  }
}
